import React from "react";

/* Small reusable code block with Copy button */
function CodeBlock({ text, label = "Copy" }) {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* ignore */
    }
  };
  return (
    <div className="stt-code">
      <pre className="stt-pre" aria-label="code snippet">
        <code>{text}</code>
      </pre>
      <button className="stt-btn stt-btn-outline" onClick={copy}>
        {copied ? "Copied!" : label}
      </button>
    </div>
  );
}

/* Output block (visually distinct from code) */
function OutputBlock({ text, label = "Output" }) {
  return (
    <div className="stt-output">
      <div className="stt-output-label">{label}</div>
      <pre className="stt-pre" aria-label="example output">
        {text}
      </pre>
    </div>
  );
}

/* Thebe-aware cell */
function ThebeCell({ code }) {
  return (
    <pre
      data-executable
      data-language="python"
      className="stt-pre"
      style={{ whiteSpace: "pre-wrap", overflowX: "auto", maxWidth: "100%" }}
    >
      {code}
    </pre>
  );
}

const colabCell = `!pip install -q softauto
# Optional extras:
# !pip install -q "softauto[imbalance]"   # SMOTE (imbalanced-learn)
# !pip install -q "softauto[boosters]"    # xgboost, lightgbm

import pandas as pd
from softauto.autorun import AutoRun

# toy dataset
df = pd.DataFrame({
    "age":[22,35,44,52,29,41,33,47],
    "city":["Pune","Mumbai","Delhi","Pune","Delhi","Mumbai","Pune","Delhi"],
    "income":[35_000,72_000,54_000,1_20_000,46_000,80_000,57_000,99_000],
    "label":[0,1,1,1,0,1,0,1]
})

run = AutoRun(
    df=df, target="label", task="classification",
    model="auto", cv=5, tune="random", n_iter=20,
    feature_selection="mutual_info", top_k_features=6,
    imbalance="auto", smote_k_neighbors=2,
    cat_min_freq=0.05, num_clip_quantiles=(0.01,0.99),
    scale="standard", plots=True,
    advisor_threshold=0.75, advisor_auto_fix=True,
)
res = run.fit()
print(res["best_model_name"], res["cv_best_score"])
print(res["metrics"])
print(res["artifacts_dir"])`;

const colabOutput = `Collecting softauto
  Downloading softauto-0.3.2-py3-none-any.whl ...
Collecting scikit-learn ...
Installing collected packages: softauto, scikit-learn, joblib, ...
Successfully installed softauto-0.3.2 scikit-learn-<ver> joblib-<ver>
random_forest 0.82
{'accuracy': 0.83, 'f1': 0.82, 'roc_auc': 0.88}
softauto_artifacts`;

const quickstartCode = `import pandas as pd
from softauto.autorun import AutoRun

df = pd.read_csv("mydata.csv")

run = AutoRun(
  df=df, target="label", task="classification",
  model="auto",
  cv=5, tune="random", n_iter=20,
  feature_selection="mutual_info", top_k_features=20,
  imbalance="auto", smote_k_neighbors=2,
  cat_min_freq=0.02, num_clip_quantiles=(0.01,0.99),
  scale="standard", plots=True,
  advisor_threshold=0.75, advisor_auto_fix=True,
)

results = run.fit()
print(results["metrics"])
print(results["best_model_name"])
print(results["artifacts_dir"])`;

const sampleAdvisor = `# advisor snippet (example)
{
  "triggered": true,
  "chosen_model_cv": 0.68,
  "threshold": 0.75,
  "suggestions": [["random_forest", 0.78], ["svm_rbf", 0.76]],
  "tips": [
    "Try scale='standard' for SVM/MLP",
    "Increase cv to 7",
    "Reduce top_k_features to 15"
  ]
}`;

const extendedExample = `from softauto.autorun import AutoRun

res = AutoRun(
  df, target="y", task="regression",
  model="auto", cv=7, tune="random", n_iter=48,
  feature_selection="rfecv",
  cat_min_freq=0.03, num_clip_quantiles=(0.02,0.98),
  scale="robust", plots=True,
  advisor_threshold=0.60, advisor_auto_fix=True,
).fit()

print(res["metrics"])   # {'r2': ..., 'rmse': ..., 'mae': ...}
print(res["artifacts_dir"])`;

const artifactsTree = `softauto_artifacts/
├─ report.html            # EDA + charts (embedded PNGs)
├─ eda.json               # dataset profile
├─ metrics.json           # holdout metrics
├─ tried_models.json      # CV shortlist with scores/hyperparams
├─ model.txt              # best model name
└─ pipeline.joblib        # full sklearn pipeline (preprocess + estimator)`;

const tipsOutput = `Tips:
- If SMOTE wasn't applied, your CV folds may be too small; lower smote_k_neighbors or keep class weights.
- Seeing ConvergenceWarning? Increase max_iter and ensure scale='standard' for SVM/MLP/LogReg.
- Low CV stability: raise cv, reduce top_k_features, prefer simpler models.`;

export default function SoftautoExamples() {
  return (
    <main className="docs-wrap">
      <div className="docs-layout">
        <aside className="docs-toc">
          <strong>On this page</strong>
          <a href="#intro">Intro</a>
          <a href="#run">Run on Colab</a>
          <a href="#quickstart">Quickstart</a>
          <a href="#advisor">Advisor Example</a>
          <a href="#artifacts">Artifacts</a>
          <a href="#extended">Extended Example</a>
          <a href="#live">Live Cell (Thebe)</a>
        </aside>

        <article>
          <header className="stt-hero">
            <div className="stt-pill">softauto • Examples</div>
            <h1 className="stt-h1">AutoML with seatbelts — examples</h1>
            <p className="stt-lead">
              Requires a Python environment with scikit-learn. Use Colab or any Jupyter server. Optional extras:
              <code> [imbalance]</code> for SMOTE and <code>[boosters]</code> for XGBoost/LightGBM.
            </p>
          </header>

          {/* Intro */}
          <section id="intro" className="stt-section">
            <p className="stt-p">
              In-browser Python (Pyodide) often lacks native deps for scikit-learn add-ons. For a smooth run,
              prefer Colab/Notebook with full wheels. All examples below mirror the docs layout.
            </p>
          </section>

          {/* Colab */}
          <section id="run" className="stt-section stt-card">
            <h2 className="stt-card-title">Run on Colab</h2>
            <div className="stt-card-body">
              <CodeBlock text={colabCell} label="Copy Colab cell" />
              <OutputBlock label="Expected Colab output (truncated)" text={colabOutput} />
              <div style={{ display: "flex", gap: ".6rem", flexWrap: "wrap", marginTop: 12 }}>
                <a className="stt-btn" href="/examples/softauto/softauto_colab.ipynb">
                  Open local notebook
                </a>
                <a
                  className="stt-btn stt-btn-outline"
                  href="https://colab.research.google.com/"
                  target="_blank"
                  rel="noreferrer"
                >
                  Open on Colab
                </a>
              </div>
            </div>
          </section>

          {/* Quickstart */}
          <section id="quickstart" className="stt-section">
            <h2 className="stt-h2">Quickstart</h2>
            <CodeBlock text={quickstartCode} label="Copy example" />
            <OutputBlock
              label="Tips"
              text={tipsOutput}
            />
          </section>

          {/* Advisor */}
          <section id="advisor" className="stt-section">
            <h2 className="stt-h2">Advisor Example</h2>
            <p className="stt-p">
              Set <code>advisor_threshold</code> to trigger guardrails; enable{" "}
              <code>advisor_auto_fix</code> to auto-switch to the best suggestion.
            </p>
            <OutputBlock label="Sample advisor payload" text={sampleAdvisor} />
          </section>

          {/* Artifacts */}
          <section id="artifacts" className="stt-section">
            <h2 className="stt-h2">Artifacts</h2>
            <OutputBlock label="Saved files" text={artifactsTree} />
            <p className="stt-p" style={{ opacity: 0.85 }}>
              Load the full preprocessing+model pipeline with{" "}
              <code>joblib.load("softauto_artifacts/pipeline.joblib")</code> and call <code>predict</code>/<code>predict_proba</code>.
            </p>
          </section>

          {/* Extended */}
          <section id="extended" className="stt-section">
            <h2 className="stt-h2">Extended Example (regression + RFECV)</h2>
            <CodeBlock text={extendedExample} label="Copy extended example" />
          </section>

          {/* Live (Thebe) */}
          <section id="live" className="stt-section stt-card">
            <h2 className="stt-card-title">Live Cell (via Thebe + Binder)</h2>
            <div className="stt-card-body">
              <ThebeCell code={quickstartCode} />
              <OutputBlock
                label="When you run it, you'll see…"
                text={`- Printed best model + metrics\n- An artifacts directory path\n- If plots=True and the environment supports it, report.html with EDA charts will be generated.`}
              />
              <p className="stt-p" style={{ opacity: 0.8 }}>
                Tip: Binder cold boots can take time. If SMOTE is requested without imbalanced-learn,
                install <code>softauto[imbalance]</code>.
              </p>
            </div>
          </section>
        </article>
      </div>
    </main>
  );
}
